import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useParams} from "react-router-dom";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControlLabel,
    FormGroup,
    Link,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {useCreateChargeMCMutation, useGetChargesMCQuery, useGetGuidMapMCQuery} from "../../../core/api/mc-charge-api";

import Loading from "../../../components/Common/Loading";
import ChargesList from "../chargesList";

import {LoadingButton, TabContext, TabPanel} from "@mui/lab";

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
import {useGetChargesTypesV2Query} from "../../../core/api/charge-api";
import DeviceBaChargerLogs from "../../ChargerLogs/ba/DeviceBaChargerLogs";
import Guid2Block from "./guid2block";
import ChargeTargetHint from "../chargeTargetHint";
import MassChargeWizard, {createGuidMapAdapter} from "../massCharge/MassChargeWizard";
import {buildChargeDataListMcDs} from "./chargeRequest";
import {types} from "../../../core/reducers/commonData";
import {useNavigate} from "react-router";
import {SnackBarContext} from "../../../snackBarProvider";
import {useCheckMCExistMutation} from "../../../core/api/mc-state-api";
import {useGetAllChargeTemplatesQuery} from "../../../core/api/charge-template-api";
import {ChargeTemplate} from "../../Templates";

dayjs.extend(utc);

const noop = () => {};

function Charger() {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const {game} = useParams()

    const CommonData = useSelector((state) => state.data.CommonData)
    const deviceId = CommonData.deviceId

    const {
        data: chargesData,
        isLoading: chargesLoading,
        isSuccess: chargesSuccess,
        isError: chargesIsError,
        error: chargesError
    } = useGetChargesMCQuery(deviceId, {skip: deviceId === ""})

    const {data: guidMap} = useGetGuidMapMCQuery()
    const {data: chargeTemplates} = useGetAllChargeTemplatesQuery(game)

    const [checkExist] = useCheckMCExistMutation();
    const [createCharge, createChargeResponse] = useCreateChargeMCMutation()

    const {
        data: chargeTypes,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetChargesTypesV2Query(game)

    const [showJson, setShowJson] = useState(false)
    const [isMassCharge, setIsMassCharge] = useState(false)
    const [formErrors, setFormErrors] = useState({})
    const [isChargeButtonDisable, setChargeButtonDisable] = useState(true);
    const [tabIndex, setTabIndex] = useState("1");
    const [selectedItemList, setSelectedItemList] = useState([{"item": {"value": "", "guidMap": "", "guidMap2": ""}}]);
    const [validateChildrenList, setValidateChildrenList] = useState([]);
    const [resetChildData, setResetChildData] = useState(false);

    const handleTabChange = (event, newTabIndex) => {
        setTabIndex(newTabIndex);
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const toggleMassCharge = () => {
        const next = !isMassCharge;
        setIsMassCharge(next)
        dispatch({
            type: types.SET_MASS_CHARGE,
            payload: {isMassCharge: next},
        });
        if (next) {
            // mass charge has its own list of users — drop the single one
            dispatch({type: types.SET_DEVICE_ID, payload: {deviceId: ""}})
            navigate(`/${game}/charger/`)
        }
    }

    const amountIsZero = () => {
        return !!selectedItemList.filter(sil =>
            sil.hasOwnProperty("amount")
            && (sil.amount === 0 || sil.amount === "" || sil.amount === "0" || !(parseInt(sil.amount) == sil.amount))
            && sil.item.amount === true
        )[0]
    }

    const checkChildrenValid = () => {
        if (validateChildrenList.length === 0) return false;
        return !validateChildrenList.includes(false)
    }

    useEffect(() => {
        setChargeButtonDisable(!checkChildrenValid() || amountIsZero());
    }, [validateChildrenList, selectedItemList])

    const clearErrors = () => {
        setFormErrors({})
    }

    const validateForm = () => {
        if (deviceId === '') {
            setFormErrors({deviceId: "не указан State ID"})
            return false
        }
        return true;
    }

    const createChargeHandler = () => {
        clearErrors()
        if (!validateForm()) return false;

        createCharge({
            "userIds": [deviceId],
            "chargeDataList": buildChargeDataListMcDs(selectedItemList)
        })
    }

    const idsToCharge = deviceId ? 1 : 0;
    const isChargeDisabled = isChargeButtonDisable || idsToCharge === 0;

    const getAsJSON = () => {
        const data = buildChargeDataListMcDs(selectedItemList).map((item) => ({
            "type": item["type"],
            ...item["data"]
        }))
        return JSON.stringify(data, null, 0)
    }

    const selectTemplate = (list) => {
        setValidateChildrenList([])
        setSelectedItemList(list)
    }

    // --- Предупреждения о незаполненных полях ---
    const getWarnings = () => {
        const warnings = [];

        selectedItemList.forEach((item, index) => {
            const label = item.item?.name || item.item?.value || `Item ${index + 1}`;

            if (item.item?.guidMap !== "" && !item.guid?.guid) {
                warnings.push(`[${label}] empty ${item.item.guidMap}`);
            }
            if (item.item?.guidMap2 !== "" && !item.guid2?.guid) {
                warnings.push(`[${label}] empty ${item.item.guidMap2}`);
            }
            if (
                item.item?.amount === true &&
                (item.amount === "" || item.amount === "0" || item.amount === 0 || item.amount === undefined)
            ) {
                warnings.push(`[${label}] empty amount`);
            }
            if (item.item?.endTime === true && !item.endTime) {
                warnings.push(`[${label}] empty End Time`);
            }
        });

        return warnings;
    }

    const warnings = getWarnings();

    const massAdapter = useMemo(() => createGuidMapAdapter(chargeTypes, guidMap), [chargeTypes, guidMap]);

    // The charge constructor in single-item mode — used by the mass charge wizard as the item picker
    const renderItemEditor = (entry, onChange) => (
        <Guid2Block
            single
            selectedItem={entry.item}
            selectedITemList={[entry]}
            setSelectedItemList={(list) => onChange(list[0])}
            index={0}
            chargeTypes={chargeTypes}
            isMassCharge
            resetData={false}
            setResetData={noop}
            validateChildrenList={[]}
            setValidateChildrenList={noop}
            setChargeButtonDisable={noop}
        />
    );

    return (
        <>
            <Card sx={{mb: 2}}>
                <CardContent>
                    <FormGroup sx={{pb: 2}}>
                        <FormControlLabel
                            control={
                                <Switch checked={isMassCharge} onChange={toggleMassCharge} name="massCharge"/>
                            }
                            label="Mass charge"
                        />
                    </FormGroup>

                    {isMassCharge && (
                        <Loading isLoading={isLoading} isError={isError} error={error} isSuccess={isSuccess}>
                            {chargeTypes !== undefined && (
                                <MassChargeWizard
                                    adapter={massAdapter}
                                    templates={chargeTemplates ?? []}
                                    buildChargeData={buildChargeDataListMcDs}
                                    checkExist={(ids) => checkExist({ids}).unwrap()}
                                    createCharge={({userIds, chargeData}) => createCharge({userIds, chargeDataList: chargeData}).unwrap()}
                                    renderItemEditor={renderItemEditor}
                                />
                            )}
                        </Loading>
                    )}

                    {!isMassCharge && <>
                        <ChargeTargetHint idsCount={idsToCharge} isMassCharge={false} onEnableMassCharge={toggleMassCharge}/>

                        <Loading
                            isLoading={isLoading}
                            isError={isError}
                            error={error}
                            isSuccess={isSuccess}>

                            {chargeTypes !== undefined && selectedItemList.map((item, index) => (
                                <Guid2Block
                                    key={index}
                                    selectedItem={item["item"]}
                                    selectedITemList={selectedItemList}
                                    setSelectedItemList={setSelectedItemList}
                                    index={index}
                                    setChargeButtonDisable={setChargeButtonDisable}
                                    isMassCharge={isMassCharge}
                                    resetData={resetChildData}
                                    setResetData={setResetChildData}
                                    addButtonDisabled={isChargeButtonDisable}
                                    validateChildrenList={validateChildrenList}
                                    setValidateChildrenList={setValidateChildrenList}
                                    chargeTypes={chargeTypes}
                                />
                            ))}
                        </Loading>

                        {Object.keys(formErrors).length > 0 &&
                            <Alert severity="error" sx={{mb: 2}}>
                                {Object.values(formErrors).map(error => (
                                    <div key={error}>{error}</div>
                                ))}
                            </Alert>
                        }

                        {/* Предупреждение о незаполненных полях */}
                        {warnings.length > 0 && (
                            <Alert severity="warning" sx={{mb: 2}}>
                                <strong>You still can charge on your own risk. But check empty fields:</strong>
                                {warnings.map((w, i) => (
                                    <div key={i}>• {w}</div>
                                ))}
                            </Alert>
                        )}

                        {createChargeResponse.isSuccess &&
                            <Alert severity="info" sx={{mb: 2}}>
                                Charge result done {createChargeResponse.data.userIds.length}:
                                {createChargeResponse.data.userIds.map(data => (
                                    <div key={data}>{data}</div>
                                ))}
                            </Alert>
                        }

                        {showJson && <Box sx={{mt: 2, mb: 4}}>
                            <TextField multiline={true} fullWidth={true} rows={5} value={getAsJSON()}/>
                            <Button sx={{mt: 1}} color={"warning"} variant={"contained"} onClick={() => {
                                navigator.clipboard.writeText(getAsJSON())
                                addAlert({text: 'JSON Copied', type: 'success'})
                            }}>Copy to clipboard</Button>
                        </Box>}

                        <Stack direction="row" spacing={2} sx={{alignItems: "center", mt: 1}}>
                            <LoadingButton
                                disabled={isChargeDisabled}
                                loading={createChargeResponse.isLoading}
                                variant={"contained"}
                                color={"success"}
                                onClick={createChargeHandler}>Charge</LoadingButton>
                            <Typography
                                variant="body2"
                                color={idsToCharge === 0 ? 'warning.main' : 'text.secondary'}
                            >
                                {idsToCharge} ids to charge
                            </Typography>
                            <Link component={"button"} variant="body2" onClick={() => setShowJson(!showJson)}>
                                {showJson ? 'Hide JSON' : 'Show JSON'}
                            </Link>
                        </Stack>

                        <ChargeTemplate
                            game={game}
                            isChargeButtonDisable={isChargeButtonDisable}
                            selectedItemList={selectedItemList}
                            setSelectedItemList={selectTemplate}/>
                    </>}
                </CardContent>
            </Card>
            {deviceId != '' && isSuccess && <TabContext value={tabIndex}>
                <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="Charges" value="1"/>
                    <Tab label="Charger Logs" value="2"/>
                </Tabs>
                <TabPanel value="1">
                    <Card>
                        <ChargesList
                            deviceId={deviceId}
                            chargesData={chargesData}
                            chargesLoading={chargesLoading}
                            chargesSuccess={chargesSuccess}
                            chargesIsError={chargesIsError}
                            chargesError={chargesError}
                            chargeTypes={chargeTypes}
                            guidMap={guidMap}
                        />
                    </Card>
                </TabPanel>
                <TabPanel value="2">
                    <DeviceBaChargerLogs deviceId={deviceId}/>
                </TabPanel>
            </TabContext>}
        </>
    );
}

export default Charger;
